<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { getApiUrl } from '../api';
  import { selectedCountry } from '../countryStore';

  export let showModal = false;
  export let preSelectedCards = [];

  const dispatch = createEventDispatcher();

  let availableCards = [];
  let selectedCards = []; 
  
  // Custom Input State
  let customCardName = '';
  
  // Pagination state
  let currentPage = 1;
  let totalPages = 1;
  const pageSize = 10;

  let isLoading = false;
  let error = '';
  let wasOpen = false;

  $: if (showModal) {
    if (!wasOpen) {
      wasOpen = true;
      initialize();
    }
  } else {
    wasOpen = false;
  }

  function initialize() {
    selectedCards = [...preSelectedCards]; 
    customCardName = '';
    currentPage = 1; 
    fetchPaginatedCards(currentPage);
  }

  async function fetchPaginatedCards(page) {
    isLoading = true;
    error = '';
    try {
      const fetchUrl = getApiUrl(`/credit-cards/list?page=${page}&pageSize=${pageSize}&countryCode=${$selectedCountry}`);
      const response = await fetch(fetchUrl);

      if (!response.ok) {
        throw new Error(`Could not load credit cards. Server responded with status: ${response.status}`);
      }
      
      const data = await response.json();
      
      availableCards = data.cards || [];
      totalPages = data.totalPages || 1;
      currentPage = data.page || 1;

    } catch (e) {
      console.error("Error fetching credit cards:", e);
      error = e.message;
    } finally {
      isLoading = false;
    }
  }

  function handleSelectAll(e) {
    const cardNamesOnPage = availableCards.map(card => card.name);
    if (e.currentTarget.checked) {
      const newSelections = new Set([...selectedCards, ...cardNamesOnPage]);
      selectedCards = Array.from(newSelections);
    } else {
      selectedCards = selectedCards.filter(name => !cardNamesOnPage.includes(name));
    }
  }

  function toggleSelection(name, isChecked) {
      if (isChecked) {
          // Add to array
          selectedCards = [...selectedCards, name];
      } else {
          // Remove from array
          selectedCards = selectedCards.filter(v => v !== name);
      }
  }

  // --- NEW: Add Custom Card Logic ---
  function addCustomCard() {
    const trimmed = customCardName.trim();
    if (trimmed && !selectedCards.includes(trimmed)) {
        selectedCards = [...selectedCards, trimmed];
        customCardName = ''; // Clear input
    }
  }

  // --- NEW: Remove Item Logic ---
  function removeCard(name) {
      selectedCards = selectedCards.filter(c => c !== name);
  }

  function confirmSelection() {
    dispatch('confirm', { selected: selectedCards });
  }

  function closeModal() {
    dispatch('close');
  }
  
  function goToPage(page) {
    if (page >= 1 && page <= totalPages) {
      fetchPaginatedCards(page);
    }
  }

  $: areAllOnPageSelected = availableCards.length > 0 && availableCards.every(card => selectedCards.includes(card.name));

</script>

{#if showModal}
  <div class="modal-backdrop" on:click={closeModal}>
    <div class="modal-content" on:click|stopPropagation>
      <div class="header">
          <h3>Select Eligible Credit Cards ({$selectedCountry})</h3>
      </div>
      
      {#if isLoading}
        <p>Loading...</p>
      {:else if error}
        <p class="error">{error}</p>
      {:else}
        <div class="card-list">
          {#if availableCards.length > 0}
              <div class="select-all">
                <input type="checkbox" id="select-all-cards" checked={areAllOnPageSelected} on:change={handleSelectAll}>
                <label for="select-all-cards">Select All on This Page</label>
              </div>
              {#each availableCards as card (card._id)}
                <div class="card-item">
                  
                  <input 
                      type="checkbox" 
                      value={card.name} 
                      checked={selectedCards.includes(card.name)} 
                      on:change={(e) => toggleSelection(card.name, e.target.checked)} 
/>
                  <label for={card._id}><strong>{card.bankName}</strong> - {card.name} ({card.issuer})</label>
                </div>
              {/each}
          {:else}
              <p class="no-data">No credit cards found for {$selectedCountry}. You can add a custom one below.</p>
          {/if}
        </div>
        
        <div class="pagination">
          <button on:click={() => goToPage(currentPage - 1)} disabled={currentPage <= 1}>Previous</button>
          <span>Page {currentPage} of {totalPages}</span>
          <button on:click={() => goToPage(currentPage + 1)} disabled={currentPage >= totalPages}>Next</button>
        </div>
      {/if}

      <!-- NEW: Custom Input Section -->
      <div class="custom-section">
          <label for="custom-card">Add Custom Card:</label>
          <div class="custom-row">
              <input type="text" id="custom-card" bind:value={customCardName} placeholder="e.g. My Local Bank Card" on:keydown={(e) => e.key === 'Enter' && addCustomCard()} />
              <button class="add-btn" on:click={addCustomCard} disabled={!customCardName.trim()}>Add</button>
          </div>
      </div>

      <!-- NEW: Selection Summary (Tags) -->
      <div class="selection-summary">
          <strong>Selected ({selectedCards.length}):</strong>
          <div class="tags">
              {#each selectedCards as card}
                  <span class="tag">
                      {card} 
                      <button class="remove-tag" on:click={() => removeCard(card)}>&times;</button>
                  </span>
              {/each}
          </div>
      </div>

      <div class="modal-actions">
        <button class="secondary" on:click={closeModal}>Cancel</button>
        <button class="primary" on:click={confirmSelection}>Confirm Selection</button>
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-backdrop {
    position: fixed; top: 0; left: 0; width: 100%; height: 100%;
    background: rgba(0, 0, 0, 0.5); display: flex;
    justify-content: center; align-items: center; z-index: 1000;
  }
  .modal-content {
    background: white; padding: 1.5rem; border-radius: 8px;
    width: 90%; max-width: 600px; max-height: 90vh;
    display: flex; flex-direction: column;
  }
  .header h3 { margin-top: 0; }

  .card-list {
    flex-grow: 1; overflow-y: auto; border: 1px solid #eee;
    padding: 1rem; border-radius: 4px; margin-bottom: 1rem; min-height: 150px;
  }
  .card-item, .select-all {
    display: flex; align-items: center; gap: 0.5rem; padding: 0.5rem 0; border-bottom: 1px solid #f9f9f9;
  }
  .select-all { font-weight: bold; border-bottom: 1px solid #ccc; margin-bottom: 0.5rem; }
  input[type="checkbox"] { width: 1.2em; height: 1.2em; }
  
  .pagination { display: flex; justify-content: space-between; align-items: center; padding: 0.5rem 0; margin-bottom: 1rem;}
  
  /* Custom Input Styles */
  .custom-section { border-top: 1px solid #eee; padding-top: 1rem; margin-bottom: 1rem; }
  .custom-section label { display: block; font-weight: 500; margin-bottom: 0.5rem; font-size: 0.9rem; }
  .custom-row { display: flex; gap: 0.5rem; }
  .custom-row input { flex-grow: 1; padding: 0.5rem; border: 1px solid #ccc; border-radius: 4px; }
  .add-btn { background-color: #28a745; color: white; border: none; padding: 0.5rem 1rem; border-radius: 4px; cursor: pointer; }
  .add-btn:disabled { background-color: #ccc; cursor: not-allowed; }

  /* Tags Styles */
  .selection-summary { margin-bottom: 1rem; font-size: 0.9rem; }
  .tags { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 0.5rem; max-height: 100px; overflow-y: auto; }
  .tag { background-color: #e9ecef; padding: 2px 8px; border-radius: 12px; display: flex; align-items: center; gap: 5px; font-size: 0.85rem; }
  .remove-tag { background: none; border: none; color: #dc3545; font-weight: bold; cursor: pointer; padding: 0; font-size: 1rem; line-height: 1; }

  .modal-actions { display: flex; justify-content: flex-end; gap: 1rem; border-top: 1px solid #eee; padding-top: 1rem; }
  .modal-actions button { padding: 0.6rem 1.2rem; border-radius: 4px; border: none; cursor: pointer; font-size: 1rem; }
  .primary { background-color: #007bff; color: white; }
  .secondary { background-color: #6c757d; color: white; }
  .error { color: red; }
  .no-data { color: #666; font-style: italic; text-align: center; margin-top: 2rem; }
</style>
